import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";

import { EntityClickHandler } from "../../types/entityRegistry";
import { ProductDetail } from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";
import { CellParts, CellSources, CellTip, NOTHING } from "./CompareParts";
import {
  CompareCell,
  CompareGroup,
  CompareGroupId,
  CompareRow,
} from "./compareModel";

const FIRST_COLUMN_WIDTH = 250;
const COLUMN_MIN_WIDTH = 240;
const COLUMN_EDGE = "1px solid rgba(255, 255, 255, 0.12)";

const TOOLTIP_SLOTS = {
  tooltip: {
    sx: {
      maxWidth: 360,
      backgroundColor: "background.raised",
      color: "text.primary",
      border: 1,
      borderColor: "rgba(255, 255, 255, 0.14)",
      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.55)",
    },
  },
} as const;

// One column of the table: a compared product as its head shows it
export interface CompareTableColumn {
  // The id the address names
  id: string;
  // What the product is called here: its name, or what stands for it while
  // it has none, loads or failed
  name: string;
  // The read; null while it loads and when it failed
  product: ProductDetail | null;
  loading: boolean;
  // Why the product cannot be shown; null while it can, or while it loads
  error: string | null;
}

interface CompareTableProps {
  columns: CompareTableColumn[];
  // The groups to show, each cell in the order of the columns
  groups: CompareGroup[];
  // The id of the column the others are set against
  referenceId: string | null;
  showSources: boolean;
  onRemove: (id: string) => void;
  onOpenEntity: EntityClickHandler;
}

// The first maker on record, where the read holds one. The read names no
// country for an organisation, so the head shows none.
const firstMaker = (product: ProductDetail) =>
  product.organizations.find((group) => group.role === "maker")?.items[0]
    ?.organization ?? null;

const ColumnHead: React.FC<{
  column: CompareTableColumn;
  isReference: boolean;
  onRemove: (id: string) => void;
  onOpenEntity: EntityClickHandler;
}> = ({ column, isReference, onRemove, onOpenEntity }) => {
  const { product } = column;
  const maker = product ? firstMaker(product) : null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            color: "text.primary",
            fontSize: 13.5,
            fontWeight: 600,
            overflowWrap: "anywhere",
          }}
        >
          {product ? (
            <MuiLink
              component="button"
              type="button"
              underline="hover"
              color="text.primary"
              onClick={() => onOpenEntity(product.id, "product")}
              sx={{
                font: "inherit",
                textAlign: "left",
                verticalAlign: "baseline",
                fontStyle: product.name ? "normal" : "italic",
              }}
            >
              {column.name}
            </MuiLink>
          ) : (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {column.loading && (
                <CircularProgress
                  size={12}
                  aria-label={`Loading ${column.name}`}
                  sx={{ mr: 0.75, verticalAlign: "middle" }}
                />
              )}
              {column.name}
            </Box>
          )}
          {isReference && (
            <Chip
              label="reference"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 0.75, height: 20, fontSize: 11.5 }}
            />
          )}
        </Box>
        <Box
          sx={{ mt: "3px", fontSize: 12, fontWeight: 400, color: "text.secondary" }}
        >
          {column.error ? (
            <Box component="span" role="alert" sx={{ color: "error.light" }}>
              {column.error}
            </Box>
          ) : !product ? (
            <>loading…</>
          ) : maker ? (
            <EntityLink id={maker.id} type={maker.type} name={maker.name} />
          ) : (
            <Box component="span" sx={{ color: "text.disabled" }}>
              no maker on record
            </Box>
          )}
        </Box>
      </Box>
      <IconButton
        size="small"
        aria-label={`Remove ${column.name}`}
        onClick={() => onRemove(column.id)}
        sx={{ mt: "-4px", mr: "-6px", color: "text.disabled" }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

// One cell: its statement, a dash for nothing on record, nothing at all for
// a product that is not loaded; the accent bar on its left edge when it
// differs from the reference; its sources on hover, and written out when
// asked for
const ValueCell: React.FC<{ cell: CompareCell; showSources: boolean }> = ({
  cell,
  showSources,
}) => {
  const content = (
    <TableCell
      data-differs={cell.differsFromReference ? "true" : undefined}
      sx={{
        borderLeft: COLUMN_EDGE,
        p: "7px 12px",
        fontSize: 12.5,
        lineHeight: 1.45,
        verticalAlign: "middle",
        color: "text.primary",
        overflowWrap: "anywhere",
        ...(cell.differsFromReference
          ? {
              boxShadow: (theme) =>
                `inset 3px 0 0 ${theme.palette.accent.main}`,
            }
          : {}),
      }}
    >
      {cell.empty ? cell.loaded && NOTHING : <CellParts parts={cell.parts} />}
      {showSources && <CellSources sources={cell.sources} />}
    </TableCell>
  );
  // The tip is a node, not a text, so that it does not rename the cell
  return cell.sources.length > 0 ? (
    <Tooltip
      placement="top"
      enterDelay={300}
      slotProps={TOOLTIP_SLOTS}
      title={<CellTip cell={cell} />}
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
};

const rowHeadSx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  backgroundColor: "background.paper",
  textAlign: "left",
  fontWeight: 400,
  fontSize: 12.5,
  p: "7px 12px",
  color: "text.primary",
  overflowWrap: "anywhere",
} as const;

const AttributeRow: React.FC<{ row: CompareRow; showSources: boolean }> = ({
  row,
  showSources,
}) => (
  <TableRow>
    <TableCell component="th" scope="row" sx={rowHeadSx}>
      {row.label}
      {row.sublabel && (
        <Box component="span" sx={{ color: "text.disabled" }}>
          {" · "}
          {row.sublabel}
        </Box>
      )}
    </TableCell>
    {row.cells.map((cell, index) => (
      <ValueCell key={index} cell={cell} showSources={showSources} />
    ))}
  </TableRow>
);

// The table: the products as columns, the attributes as rows in groups that
// fold. It scrolls with its head and its first column fixed.
const CompareTable: React.FC<CompareTableProps> = ({
  columns,
  groups,
  referenceId,
  showSources,
  onRemove,
  onOpenEntity,
}) => {
  // The groups folded; the page's own, not part of the address
  const [folded, setFolded] = useState<ReadonlySet<CompareGroupId>>(new Set());
  const toggle = (id: CompareGroupId) =>
    setFolded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });

  return (
    <Box
      sx={{
        overflow: "auto",
        maxHeight: "calc(100vh - 120px)",
        minHeight: 160,
        position: "relative",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        backgroundColor: "background.paper",
      }}
    >
      <Table
        stickyHeader
        size="small"
        aria-label="Attributes by product"
        sx={{
          borderCollapse: "separate",
          borderSpacing: 0,
          tableLayout: "fixed",
          width: "100%",
          minWidth: FIRST_COLUMN_WIDTH + columns.length * COLUMN_MIN_WIDTH,
          "& .MuiTableCell-root": {
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          },
        }}
      >
        <colgroup>
          <col style={{ width: FIRST_COLUMN_WIDTH }} />
          {columns.map((column) => (
            <col key={column.id} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              scope="col"
              sx={{
                backgroundColor: "background.paper",
                left: 0,
                zIndex: 4,
                textAlign: "left",
                verticalAlign: "bottom",
                pl: "12px",
                fontSize: 12,
                fontWeight: 400,
                color: "text.disabled",
              }}
            >
              attribute
            </TableCell>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                scope="col"
                sx={{
                  backgroundColor: "background.paper",
                  textAlign: "left",
                  verticalAlign: "top",
                  p: "10px 12px",
                  borderLeft: COLUMN_EDGE,
                }}
              >
                <ColumnHead
                  column={column}
                  isReference={column.id === referenceId}
                  onRemove={onRemove}
                  onOpenEntity={onOpenEntity}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            const isFolded = folded.has(group.id);
            return (
              <React.Fragment key={group.id}>
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    sx={{
                      backgroundColor: "background.paper",
                      borderTop: COLUMN_EDGE,
                      p: "6px 8px 4px",
                      textAlign: "left",
                      fontSize: 10.5,
                      fontWeight: 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      color: "text.disabled",
                    }}
                  >
                    {/* Fixed to the left edge, so that it stays in sight
                        while the table scrolls sideways */}
                    <Box sx={{ position: "sticky", left: 8, display: "inline-block" }}>
                      <ButtonBase
                        onClick={() => toggle(group.id)}
                        aria-expanded={!isFolded}
                        sx={{
                          font: "inherit",
                          letterSpacing: "inherit",
                          textTransform: "inherit",
                          color: "text.secondary",
                          gap: 0.75,
                        }}
                      >
                        {group.label}
                        <span aria-hidden>{isFolded ? "▸" : "▾"}</span>
                      </ButtonBase>
                      {group.note && (
                        <Box
                          component="span"
                          sx={{ textTransform: "none", letterSpacing: 0, fontSize: 11.5 }}
                        >
                          {" · "}
                          {group.note}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                {!isFolded &&
                  group.rows.map((row) => (
                    <AttributeRow
                      key={row.key}
                      row={row}
                      showSources={showSources}
                    />
                  ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default CompareTable;
